"use client";

import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface CookiePolicyContentProps {
  lastUpdated: string;
  structuredData: Record<string, unknown>;
}

export function CookiePolicyContent({
  lastUpdated,
  structuredData,
}: CookiePolicyContentProps) {
  return (
    <div className="min-h-screen bg-background">
      {/* Structured Data */}
      <script
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData),
        }}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
        type="application/ld+json"
      />

      <div className="container mx-auto max-w-4xl px-4 py-16">
        {/* Header */}
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <Badge className="mb-4" variant="outline">
            Legal Document
          </Badge>
          <h1 className="mb-6 font-bold font-heading text-4xl text-foreground md:text-5xl">
            Cookie Policy
          </h1>
          <p className="body-text-base mx-auto mb-8 max-w-2xl text-muted-foreground text-optimal">
            How WillPower Store uses cookies on our website, your rights, and
            how to manage your cookie preferences.
          </p>

          {/* Business Info Card */}
          <Card className="mx-auto max-w-2xl">
            <CardContent className="pt-6">
              <div className="grid gap-6 text-left md:grid-cols-2">
                <div>
                  <h4 className="mb-2 font-medium text-foreground">
                    Business Details
                  </h4>
                  <div className="space-y-1 text-muted-foreground text-sm">
                    <p>WillPower Store</p>
                    <p>13 Great Western Arcade</p>
                    <p>Birmingham, B32QD</p>
                    <p>United Kingdom</p>
                  </div>
                </div>
                <div>
                  <h4 className="mb-2 font-medium text-foreground">
                    Contact Information
                  </h4>
                  <div className="space-y-1 text-muted-foreground text-sm">
                    <p>Email: willpowerwellbeing@hotmail.com</p>
                    <p>Instagram: @willpowergymstoreuk</p>
                    <p>Website: willpower-store.com</p>
                    <p>Last Updated: {lastUpdated}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Cookie Policy Sections */}
        <div className="space-y-12">
          {/* Section 1: What Are Cookies */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
              1. What Are Cookies
            </h2>
            <div className="space-y-6">
              <p className="text-muted-foreground text-optimal leading-relaxed">
                Cookies are small text files that are stored on your device
                (computer, tablet, or mobile) when you visit our website. They
                help us provide you with a better browsing experience and allow
                us to analyse how our website is used.
              </p>
              <Card>
                <CardContent className="pt-6">
                  <h4 className="mb-3 font-medium text-foreground">
                    How Cookies Work
                  </h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li>
                      • Cookies contain information about your visit to our
                      website
                    </li>
                    <li>
                      • They remember your preferences and improve your
                      experience
                    </li>
                    <li>
                      • Some cookies are essential for our website to function
                      properly
                    </li>
                    <li>• Others help us understand how you use our website</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </motion.section>

          <Separator />

          {/* Section 2: How We Use Cookies */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
              2. How We Use Cookies
            </h2>
            <div className="space-y-6">
              <p className="text-muted-foreground text-optimal leading-relaxed">
                As a supplement reseller, we use cookies to enhance your
                shopping experience and improve our services:
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="mb-3 font-medium text-foreground">
                      Essential Functions
                    </h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Shopping basket functionality</li>
                      <li>• Secure checkout process</li>
                      <li>• User authentication</li>
                      <li>• Website security</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="mb-3 font-medium text-foreground">
                      Analytics & Improvement
                    </h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Understanding website usage patterns</li>
                      <li>• Improving our product offerings</li>
                      <li>• Optimising website performance</li>
                      <li>• Enhancing user experience</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </motion.section>

          <Separator />

          {/* Section 3: Types of Cookies We Use */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
              3. Types of Cookies We Use
            </h2>
            <div className="space-y-8">
              <p className="text-muted-foreground text-optimal leading-relaxed">
                We categorise our cookies based on their purpose and legal
                requirements:
              </p>

              {/* Essential Cookies */}
              <Card>
                <CardContent className="pt-6">
                  <div className="mb-4 flex items-center gap-3">
                    <h3 className="font-medium text-foreground text-lg">
                      Essential Cookies
                    </h3>
                    <Badge variant="secondary">No Consent Required</Badge>
                  </div>
                  <p className="mb-4 text-muted-foreground text-sm">
                    These cookies are necessary for our website to function and
                    cannot be switched off.
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <h4 className="mb-2 font-medium text-foreground">
                        Shopping Functions
                      </h4>
                      <ul className="space-y-1 text-muted-foreground text-xs">
                        <li>• Shopping basket contents</li>
                        <li>• Checkout process</li>
                        <li>• Payment security</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="mb-2 font-medium text-foreground">
                        Security & Performance
                      </h4>
                      <ul className="space-y-1 text-muted-foreground text-xs">
                        <li>• Session management</li>
                        <li>• Fraud prevention</li>
                        <li>• Load balancing</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Analytics Cookies */}
              <Card>
                <CardContent className="pt-6">
                  <div className="mb-4 flex items-center gap-3">
                    <h3 className="font-medium text-foreground text-lg">
                      Analytics Cookies
                    </h3>
                    <Badge variant="outline">Consent Required</Badge>
                  </div>
                  <p className="mb-4 text-muted-foreground text-sm">
                    These cookies help us understand how visitors interact with
                    our website.
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <h4 className="mb-2 font-medium text-foreground">
                        Vercel Analytics
                      </h4>
                      <ul className="space-y-1 text-muted-foreground text-xs">
                        <li>• Page views and user journeys</li>
                        <li>• Popular products and categories</li>
                        <li>• Website performance metrics</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="mb-2 font-medium text-foreground">
                        Shopify Analytics
                      </h4>
                      <ul className="space-y-1 text-muted-foreground text-xs">
                        <li>• E-commerce insights</li>
                        <li>• Conversion tracking</li>
                        <li>• Customer behaviour analysis</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Marketing Cookies */}
              <Card>
                <CardContent className="pt-6">
                  <div className="mb-4 flex items-center gap-3">
                    <h3 className="font-medium text-foreground text-lg">
                      Marketing Cookies
                    </h3>
                    <Badge variant="outline">Consent Required</Badge>
                  </div>
                  <p className="mb-4 text-muted-foreground text-sm">
                    These cookies are used to deliver relevant advertisements
                    and track campaign effectiveness.
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <h4 className="mb-2 font-medium text-foreground">
                        Social Media
                      </h4>
                      <ul className="space-y-1 text-muted-foreground text-xs">
                        <li>• Facebook Pixel for targeted ads</li>
                        <li>• Instagram integration</li>
                        <li>• Social sharing functionality</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="mb-2 font-medium text-foreground">
                        Advertising
                      </h4>
                      <ul className="space-y-1 text-muted-foreground text-xs">
                        <li>• Google Ads tracking</li>
                        <li>• Retargeting campaigns</li>
                        <li>• Email marketing pixels</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.section>

          <Separator />

          {/* Section 4: Third-Party Cookies */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
              4. Third-Party Cookies
            </h2>
            <div className="space-y-6">
              <p className="text-muted-foreground text-optimal leading-relaxed">
                As a reseller using various platforms and services, our website
                may contain cookies from third-party providers:
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="mb-3 font-medium text-foreground">
                      E-commerce Platform
                    </h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Shopify (our e-commerce platform)</li>
                      <li>• Shopify Payments (payment processing)</li>
                      <li>
                        • Shipping and logistics partners (when available)
                      </li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="mb-3 font-medium text-foreground">
                      Analytics & Marketing
                    </h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Vercel Analytics (website analytics)</li>
                      <li>• Instagram (social media integration)</li>
                      <li>• OpenTelemetry (performance monitoring)</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <p className="text-amber-800 text-sm">
                  <strong>Important:</strong> These third-party providers have
                  their own cookie policies. We recommend reviewing their
                  privacy policies to understand how they use cookies and
                  process your data.
                </p>
              </div>
            </div>
          </motion.section>

          <Separator />

          {/* Section 5: Managing Your Cookie Preferences */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
              5. Managing Your Cookie Preferences
            </h2>
            <div className="space-y-6">
              <p className="text-muted-foreground text-optimal leading-relaxed">
                You have control over which cookies you accept. Here's how to
                manage your preferences:
              </p>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="mb-3 font-medium text-foreground">
                      Our Cookie Banner
                    </h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Appears on your first visit</li>
                      <li>• Choose which cookies to accept</li>
                      <li>• Save your preferences</li>
                      <li>• Change settings anytime</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="mb-3 font-medium text-foreground">
                      Cookie Preferences
                    </h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Link in website footer</li>
                      <li>• Update consent choices</li>
                      <li>• View cookie details</li>
                      <li>• Withdraw consent</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="mb-3 font-medium text-foreground">
                      Browser Settings
                    </h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Block all cookies</li>
                      <li>• Delete existing cookies</li>
                      <li>• Set cookie preferences</li>
                      <li>• Private browsing mode</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              <Card>
                <CardContent className="pt-6">
                  <h4 className="mb-4 font-medium text-foreground">
                    Browser-Specific Instructions
                  </h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <h5 className="mb-2 font-medium text-foreground">
                        Desktop Browsers
                      </h5>
                      <ul className="space-y-1 text-muted-foreground text-sm">
                        <li>
                          <strong>Chrome:</strong> Settings → Privacy and
                          Security → Cookies
                        </li>
                        <li>
                          <strong>Firefox:</strong> Preferences → Privacy &
                          Security
                        </li>
                        <li>
                          <strong>Safari:</strong> Preferences → Privacy
                        </li>
                        <li>
                          <strong>Edge:</strong> Settings → Privacy and Services
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="mb-2 font-medium text-foreground">
                        Mobile Browsers
                      </h5>
                      <ul className="space-y-1 text-muted-foreground text-sm">
                        <li>
                          <strong>Chrome Mobile:</strong> Menu → Settings → Site
                          Settings
                        </li>
                        <li>
                          <strong>Safari iOS:</strong> Settings → Safari →
                          Privacy
                        </li>
                        <li>
                          <strong>Firefox Mobile:</strong> Menu → Settings →
                          Privacy
                        </li>
                        <li>
                          <strong>Samsung Internet:</strong> Menu → Settings →
                          Privacy
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.section>

          <Separator />

          {/* Section 6: Cookie Consent and Legal Basis */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
              6. Cookie Consent and Legal Basis
            </h2>
            <div className="space-y-6">
              <p className="text-muted-foreground text-optimal leading-relaxed">
                We process cookies under different legal bases depending on
                their purpose and UK data protection law:
              </p>
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="mb-3 font-medium text-foreground">
                      Legitimate Interests
                    </h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Essential website functionality</li>
                      <li>• Security and fraud prevention</li>
                      <li>• Website performance</li>
                      <li>• Basic analytics</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="mb-3 font-medium text-foreground">
                      Consent
                    </h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Marketing and advertising cookies</li>
                      <li>• Social media integration</li>
                      <li>• Advanced analytics</li>
                      <li>• Personalisation features</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="mb-3 font-medium text-foreground">
                      Legal Obligation
                    </h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Tax and accounting records</li>
                      <li>• Regulatory compliance</li>
                      <li>• Consumer protection</li>
                      <li>• Health and safety requirements</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Your Rights:</strong> You can withdraw consent for
                  non-essential cookies at any time through our cookie
                  preferences centre or by contacting us directly. This will not
                  affect the lawfulness of processing based on consent before
                  withdrawal.
                </p>
              </div>
            </div>
          </motion.section>

          <Separator />

          {/* Section 7: Data Retention */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
              7. Data Retention and Cookie Lifespan
            </h2>
            <div className="space-y-6">
              <p className="text-muted-foreground text-optimal leading-relaxed">
                Different cookies have different lifespans depending on their
                purpose and type:
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="mb-3 font-medium text-foreground">
                      Session Cookies
                    </h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Deleted when you close your browser</li>
                      <li>• Used for shopping basket functionality</li>
                      <li>• Essential for secure checkout</li>
                      <li>• No long-term data storage</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="mb-3 font-medium text-foreground">
                      Persistent Cookies
                    </h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• Analytics cookies: 24 months (Vercel default)</li>
                      <li>• Shopify cookies: As per Shopify's policy</li>
                      <li>• Preference cookies: 12 months</li>
                      <li>• Security cookies: 30 days</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              <div className="rounded-lg bg-muted/30 p-4">
                <p className="text-muted-foreground text-sm">
                  <strong>Note:</strong> You can delete cookies manually through
                  your browser settings at any time. Some cookies may be
                  recreated when you revisit our website if they are essential
                  for functionality.
                </p>
              </div>
            </div>
          </motion.section>

          <Separator />

          {/* Section 8: Updates to This Policy */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
              8. Updates to This Cookie Policy
            </h2>
            <div className="space-y-6">
              <p className="text-muted-foreground text-optimal leading-relaxed">
                We may update this cookie policy from time to time to reflect
                changes in our practices, technology, or legal requirements:
              </p>
              <Card>
                <CardContent className="pt-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <h4 className="mb-3 font-medium text-foreground">
                        When We Update
                      </h4>
                      <ul className="space-y-1 text-muted-foreground text-sm">
                        <li>• Changes to our website functionality</li>
                        <li>• New third-party integrations</li>
                        <li>• Updates to legal requirements</li>
                        <li>• Changes to our business practices</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="mb-3 font-medium text-foreground">
                        How We Notify You
                      </h4>
                      <ul className="space-y-1 text-muted-foreground text-sm">
                        <li>• Updated date at top of this policy</li>
                        <li>• Notice on our website</li>
                        <li>• Email notification for significant changes</li>
                        <li>• New cookie consent banner if required</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.section>
        </div>

        {/* Contact Section */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <Card className="mx-auto max-w-2xl">
            <CardContent className="pt-8 pb-8">
              <h3 className="mb-4 font-heading font-medium text-foreground text-xl">
                Questions About Our Cookie Policy?
              </h3>
              <p className="mx-auto mb-6 max-w-2xl text-muted-foreground text-optimal">
                If you have any questions about how we use cookies or need help
                managing your cookie preferences, please contact us via email or
                Instagram.
              </p>
              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <Button asChild size="lg">
                  <a href="mailto:willpowerwellbeing@hotmail.com">Email Us</a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a
                    href="https://instagram.com/willpowergymstoreuk"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Instagram DM
                  </a>
                </Button>
              </div>
              <div className="mx-auto mt-4 max-w-lg text-muted-foreground text-sm">
                <p>
                  <strong>Response Times:</strong> We usually reply within 48
                  hours for both email and Instagram messages.
                </p>
              </div>
              <div className="mt-6 border-border border-t pt-6">
                <p className="text-muted-foreground text-xs">
                  This cookie policy complies with UK GDPR and the Privacy and
                  Electronic Communications Regulations (PECR). Last updated:{" "}
                  {lastUpdated}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
